/**
 * Helper para validación de archivos antes de subir a Azure Blob Storage
 *
 * @description Implementa validaciones de seguridad para prevenir carga de archivos maliciosos
 * Mitiga vulnerabilidad WEB-GLOBALSYDE-22082025-M-1
 *
 * Validaciones implementadas:
 * - Extensión de archivo contra lista blanca
 * - Tipo MIME contra lista blanca
 * - Tamaño máximo de archivo
 * - Firma del archivo (magic numbers)
 * - Sanitización de nombre de archivo
 * - Detección de patrones sospechosos
 *
 * @see docs/SECURITY_FILE_UPLOAD_MITIGATION_PLAN.md
 */

import { AllowedFileTypes, FileContext } from '../config/allowedFileTypes'

export interface FileValidationResult {
  isValid: boolean
  errors: string[]
  sanitizedFileName: string
  warnings?: string[]
}

/**
 * Magic numbers (firmas de archivo) para verificar tipo real del archivo
 * Lee los primeros bytes para asegurar que el archivo es realmente del tipo declarado
 */
const FILE_SIGNATURES: Record<string, number[]> = {
  pdf: [0x25, 0x50, 0x44, 0x46], // %PDF
  png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], // PNG
  jpg: [0xff, 0xd8, 0xff], // JPEG
  jpeg: [0xff, 0xd8, 0xff],
  gif: [0x47, 0x49, 0x46, 0x38], // GIF8
  zip: [0x50, 0x4b, 0x03, 0x04], // PK
  docx: [0x50, 0x4b, 0x03, 0x04], // DOCX es ZIP
  xlsx: [0x50, 0x4b, 0x03, 0x04], // XLSX es ZIP
  xlsm: [0x50, 0x4b, 0x03, 0x04], // XLSM es ZIP
  xml: [0x3c, 0x3f, 0x78, 0x6d, 0x6c], // <?xml
  xsd: [0x3c, 0x3f, 0x78, 0x6d, 0x6c], // <?xml (XSD es XML)
  html: [0x3c], // < (puede ser <!DOCTYPE, <html, etc.)
  xsig: [0x3c, 0x3f, 0x78, 0x6d, 0x6c], // <?xml (XSIG es XML Signature)
  csv: [0xef, 0xbb, 0xbf] // BOM UTF-8 (opcional en CSV, común desde Excel)
}

/**
 * Extensiones explícitamente prohibidas por razones de seguridad
 * Archivos ejecutables, scripts y otros tipos peligrosos
 */
const DANGEROUS_EXTENSIONS = [
  'exe',
  'dll',
  'bat',
  'cmd',
  'com',
  'pif',
  'scr',
  'vbs',
  'js',
  'msi',
  'jar',
  'ps1',
  'sh',
  'app',
  'deb',
  'rpm',
  'dmg',
  'pkg',
  'asp',
  'aspx',
  'php',
  'jsp',
  'py',
  'rb',
  'pl',
  'cgi',
  'wsf',
  'reg',
  'msp',
  'hta',
  'cpl',
  'msc',
  'lnk'
]

/**
 * Valida un archivo completo antes de subir
 *
 * @param file - Archivo a validar
 * @param context - Contexto de negocio (determina reglas a aplicar)
 * @param maxSizeMB - Tamaño máximo opcional (override)
 * @returns Resultado de validación con errores si aplica
 */
export const validateFile = async (
  file: File,
  context: FileContext,
  maxSizeMB?: number
): Promise<FileValidationResult> => {
  const errors: string[] = []
  const warnings: string[] = []

  // 1. Validar nombre de archivo y sanitizar
  const sanitizedFileName = sanitizeFileName(file.name)

  if (sanitizedFileName !== file.name) {
    warnings.push('El nombre del archivo fue modificado por seguridad')
  }

  // 2. Validar extensión
  const extension = getFileExtension(file.name)

  if (!extension) {
    errors.push('El archivo debe tener una extensión válida')
  } else {
    if (!isExtensionAllowed(extension, context)) {
      errors.push(`La extensión .${extension} no está permitida en este contexto`)
    }

    if (DANGEROUS_EXTENSIONS.includes(extension.toLowerCase())) {
      errors.push(`La extensión .${extension} está prohibida por razones de seguridad`)
    }
  }

  // 3. Validar tamaño
  const maxSize = maxSizeMB || AllowedFileTypes[context].maxSizeMB
  const fileSizeMB = file.size / (1024 * 1024)

  if (fileSizeMB > maxSize) {
    errors.push(
      `El archivo excede el tamaño máximo de ${maxSize}MB (actual: ${fileSizeMB.toFixed(2)}MB)`
    )
  }

  // 4. Validar tipo MIME
  if (!file.type) {
    warnings.push('El tipo MIME del archivo no está especificado')
  } else if (!isMimeTypeAllowed(file.type, context)) {
    errors.push(`El tipo MIME ${file.type} no está permitido en este contexto`)
  }

  // 5. Validar firma del archivo (magic numbers) - async
  if (extension) {
    const signatureValid = await validateFileSignature(file, extension)
    if (!signatureValid) {
      errors.push('La firma del archivo no coincide con su extensión. Posible archivo malicioso')
    }
  }

  // 6. Validar contenido del nombre (patrones sospechosos)
  if (containsSuspiciousPatterns(file.name)) {
    errors.push('El nombre del archivo contiene patrones sospechosos y fue rechazado')
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedFileName,
    warnings: warnings.length > 0 ? warnings : undefined
  }
}

/**
 * Sanitiza nombre de archivo para prevenir ataques
 *
 * Previene:
 * - Path traversal (../)
 * - Inyección de scripts
 * - Caracteres especiales del sistema
 * - Nombres excesivamente largos
 *
 * @param fileName - Nombre original del archivo
 * @returns Nombre sanitizado y seguro
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return 'file'

  // Remover path traversal
  fileName = fileName.replace(/\.\./g, '')
  fileName = fileName.replace(/[/\\]/g, '')

  // Remover caracteres especiales peligrosos
  fileName = fileName.replace(/[<>:"|?*\x00-\x1f]/g, '')

  // Reemplazar caracteres no seguros manteniendo puntos para extensión
  const lastDotIndex = fileName.lastIndexOf('.')
  let nameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : ''

  // Limpiar el nombre (sin la extensión)
  nameWithoutExt = nameWithoutExt.replace(/[^a-zA-Z0-9._\-\s]/g, '_')
  nameWithoutExt = nameWithoutExt.replace(/\s+/g, '_') // Espacios a underscores
  nameWithoutExt = nameWithoutExt.replace(/_+/g, '_') // Múltiples underscores a uno

  // Limpiar la extensión
  let cleanExtension = extension.replace(/[^a-zA-Z0-9.]/g, '')

  // Limitar longitud total manteniendo extensión
  const maxLength = 100
  if ((nameWithoutExt + cleanExtension).length > maxLength) {
    nameWithoutExt = nameWithoutExt.substring(0, maxLength - cleanExtension.length)
  }

  // Asegurar que no quede vacío
  if (!nameWithoutExt) {
    nameWithoutExt = 'file'
  }

  return (nameWithoutExt + cleanExtension).trim()
}

/**
 * Obtiene extensión de archivo en minúsculas
 *
 * @param fileName - Nombre del archivo
 * @returns Extensión sin el punto, en minúsculas
 */
export const getFileExtension = (fileName: string): string => {
  if (!fileName) return ''
  const lastDot = fileName.lastIndexOf('.')
  return lastDot > 0 ? fileName.substring(lastDot + 1).toLowerCase() : ''
}

/**
 * Verifica si la extensión está permitida en el contexto dado
 *
 * @param extension - Extensión a validar (sin punto)
 * @param context - Contexto de negocio
 * @returns true si está permitida
 */
export const isExtensionAllowed = (extension: string, context: FileContext): boolean => {
  const allowed = AllowedFileTypes[context]
  return allowed.extensions.some(ext => ext.toLowerCase() === `.${extension.toLowerCase()}`)
}

/**
 * Verifica si el MIME type está permitido en el contexto
 *
 * @param mimeType - Tipo MIME del archivo
 * @param context - Contexto de negocio
 * @returns true si está permitido
 */
export const isMimeTypeAllowed = (mimeType: string, context: FileContext): boolean => {
  if (!mimeType) return false
  const allowed = AllowedFileTypes[context]
  return allowed.mimeTypes.some(type => type.toLowerCase() === mimeType.toLowerCase())
}

/**
 * Valida la firma del archivo leyendo los primeros bytes (magic numbers)
 * Asegura que el contenido real coincida con la extensión declarada
 *
 * @param file - Archivo a validar
 * @param extension - Extensión declarada
 * @returns true si la firma coincide o no hay firma conocida
 */
export const validateFileSignature = async (file: File, extension: string): Promise<boolean> => {
  try {
    const expectedSignature = FILE_SIGNATURES[extension.toLowerCase()]

    // Si no tenemos firma conocida para esta extensión, permitir
    // (solo validamos extensión y MIME type)
    if (!expectedSignature) {
      return true
    }

    // Leer primeros bytes del archivo
    const bytes = await readFileBytes(file, expectedSignature.length)

    // Comparar con firma esperada
    for (let i = 0; i < expectedSignature.length; i++) {
      if (bytes[i] !== expectedSignature[i]) {
        return false
      }
    }

    return true
  } catch (error) {
    console.error('Error validating file signature:', error)
    // En caso de error, ser conservador y rechazar
    return false
  }
}

/**
 * Lee los primeros n bytes de un archivo
 *
 * @param file - Archivo a leer
 * @param numBytes - Número de bytes a leer
 * @returns Array de bytes
 */
const readFileBytes = (file: File, numBytes: number): Promise<number[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    const blob = file.slice(0, numBytes)

    reader.onload = e => {
      const arrayBuffer = e.target?.result as ArrayBuffer
      if (!arrayBuffer) {
        reject(new Error('No se pudo leer el archivo'))
        return
      }
      const bytes = new Uint8Array(arrayBuffer)
      resolve(Array.from(bytes))
    }

    reader.onerror = () => reject(new Error('Error leyendo el archivo'))
    reader.readAsArrayBuffer(blob)
  })
}

/**
 * Detecta patrones sospechosos en nombres de archivo
 *
 * Detecta:
 * - Path traversal (..)
 * - Scripts XSS (<script>, javascript:)
 * - Event handlers (onclick=, onerror=)
 * - Null bytes
 * - Encoding malicioso
 *
 * @param fileName - Nombre a validar
 * @returns true si contiene patrones sospechosos
 */
export const containsSuspiciousPatterns = (fileName: string): boolean => {
  const suspiciousPatterns = [
    /\.\./, // Path traversal
    /<script/i, // XSS
    /javascript:/i, // XSS
    /on\w+\s*=/i, // Event handlers (onclick=, etc)
    /\0/, // Null bytes
    /%00/, // Encoded null
    /&#/, // HTML entities
    /\\x[0-9a-f]{2}/i, // Hex encoding
    /eval\(/i, // eval()
    /expression\(/i // CSS expression()
  ]

  return suspiciousPatterns.some(pattern => pattern.test(fileName))
}

/**
 * Calcula hash SHA-256 del archivo para tracking y auditoría
 *
 * @param file - Archivo a hashear
 * @returns Hash en formato hexadecimal
 */
export const calculateFileHash = async (file: File): Promise<string> => {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  } catch (error) {
    console.error('Error calculating file hash:', error)
    return ''
  }
}

/**
 * Valida múltiples archivos en lote
 *
 * @param files - Array de archivos a validar
 * @param context - Contexto de negocio
 * @returns Array de resultados de validación
 */
export const validateFiles = async (
  files: File[],
  context: FileContext
): Promise<FileValidationResult[]> => {
  const results = await Promise.all(files.map(file => validateFile(file, context)))
  return results
}

/**
 * Separa archivos válidos e inválidos después de validación
 *
 * @param files - Archivos originales
 * @param validationResults - Resultados de validación
 * @returns Objeto con archivos válidos e inválidos
 */
export const separateValidFiles = (
  files: File[],
  validationResults: FileValidationResult[]
): {
  validFiles: { file: File; sanitizedName: string }[]
  invalidFiles: { file: File; errors: string[] }[]
} => {
  const validFiles: { file: File; sanitizedName: string }[] = []
  const invalidFiles: { file: File; errors: string[] }[] = []

  files.forEach((file, index) => {
    const result = validationResults[index]
    if (result.isValid) {
      validFiles.push({
        file,
        sanitizedName: result.sanitizedFileName
      })
    } else {
      invalidFiles.push({
        file,
        errors: result.errors
      })
    }
  })

  return { validFiles, invalidFiles }
}
